import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Currency } from "../../currency/objects/currency";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_CUSTOM_INSURANCE_LIST_CONFIGURATION, EMPTY_CUSTOM_INSURANCE_LIST_CRITERIA } from "../constant/customInsuranceConstant";
import { Column } from "../objects/column";
import { ConditionField } from "../objects/conditionField";
import { CustomInsurance, CustomInsuranceContract } from "../objects/customInsurance";
import { CustomInsuranceListItem } from "../objects/customInsuranceList";
import { CustomInsuranceState } from "../objects/customInsuranceState";
import { CustomInsuranceListCriteria } from "../objects/customInsuranceTypeList";
import { InsuranceProviderOption } from "../objects/insuranceProviderOption";
import { setCustomInsurancePreference } from "../utils/setCustomInsurancePreference";

const initialState: CustomInsuranceState = {
    custom_insurance_list: [],
    custom_insurance_list_configuration: EMPTY_CUSTOM_INSURANCE_LIST_CONFIGURATION,
    custom_insurance_list_loading: false,
    custom_insurance_list_count: 0,
    custom_insurance_list_criteria: EMPTY_CUSTOM_INSURANCE_LIST_CRITERIA,
    custom_insurance: null,
    first_preference_loaded: false,
    custom_insurance_list_loaded: false,
    custom_insurance_list_refresh: false,
    custom_insurance_is_creating: false,
    custom_insurance_providers: [],
    custom_insurance_currencies: [],
    custom_insurance_condition_fields: [],
    selected_contract: null,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const updateCustomInsuranceIsCreatingReducer = (state: CustomInsuranceState, action: PayloadAction<boolean>): CustomInsuranceState => ({
    ...state, custom_insurance_is_creating: action.payload
});
const addCustomInsuranceListConfigurationPreferenceReducer = (state: CustomInsuranceState, action: PayloadAction<Preferences[]>): CustomInsuranceState => ({
    ...state, custom_insurance_list_configuration: { ...state.custom_insurance_list_configuration, preferences: [...state.custom_insurance_list_configuration.preferences, ...action.payload] }
});
const setCustomInsuranceListConfigurationPreferenceReducer = (state: CustomInsuranceState, action: PayloadAction<Preferences[]>): CustomInsuranceState => ({
    ...state, custom_insurance_list_configuration: { ...state.custom_insurance_list_configuration, preferences: action.payload }
});
const setPreferenceReducer = (state: CustomInsuranceState, action: PayloadAction<Preferences[]>): CustomInsuranceState => {
    const { preferences, columns, order_by, order } = setCustomInsurancePreference(action.payload, state.custom_insurance_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        custom_insurance_list_configuration: { ...state.custom_insurance_list_configuration, preferences, columns },
        custom_insurance_list_criteria: { ...state.custom_insurance_list_criteria, order, order_by }
    };
};
const toggleColumnReducer = (state: CustomInsuranceState, action: PayloadAction<number>): CustomInsuranceState => {
    const columns = cloneDeep([...state.custom_insurance_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, custom_insurance_list_configuration: { ...state.custom_insurance_list_configuration, columns } };
};
const setColumnsReducer = (state: CustomInsuranceState, action: PayloadAction<Column[]>): CustomInsuranceState => ({
    ...state, custom_insurance_list_configuration: { ...state.custom_insurance_list_configuration, columns: action.payload }
});
const toggleCustomInsuranceRefreshListReducer = (state: CustomInsuranceState): CustomInsuranceState => ({
    ...state, custom_insurance_list_refresh: !state.custom_insurance_list_refresh, custom_insurance_list_loaded: false
});
const updateCustomInsuranceListReducer = (state: CustomInsuranceState, action: PayloadAction<CustomInsuranceListItem[]>): CustomInsuranceState => ({
    ...state, custom_insurance_list: action.payload, custom_insurance_list_loaded: true
});
const updateCustomInsuranceListCriteriaReducer = (state: CustomInsuranceState, action: PayloadAction<CustomInsuranceListCriteria>): CustomInsuranceState => ({
    ...state, custom_insurance_list_criteria: action.payload
});
const updateCustomInsuranceReducer = (state: CustomInsuranceState, action: PayloadAction<CustomInsurance | null>): CustomInsuranceState => ({
    ...state, custom_insurance: action.payload
});
const updateCustomInsuranceListLoadingReducer = (state: CustomInsuranceState, action: PayloadAction<boolean>): CustomInsuranceState => ({
    ...state, custom_insurance_list_loading: action.payload
});
const updateCustomInsuranceListCountReducer = (state: CustomInsuranceState, action: PayloadAction<number>): CustomInsuranceState => ({
    ...state, custom_insurance_list_count: action.payload
});
const updateCustomInsuranceProvidersReducer = (state: CustomInsuranceState, action: PayloadAction<InsuranceProviderOption[]>): CustomInsuranceState => ({
    ...state, custom_insurance_providers: action.payload
});
const updateCustomInsuranceCurrenciesReducer = (state: CustomInsuranceState, action: PayloadAction<Currency[]>): CustomInsuranceState => ({
    ...state, custom_insurance_currencies: action.payload
});
const updateCustomInsuranceConditionFieldsReducer = (state: CustomInsuranceState, action: PayloadAction<ConditionField[]>): CustomInsuranceState => ({
    ...state, custom_insurance_condition_fields: action.payload
});
const updateSelectedContractReducer = (state: CustomInsuranceState, action: PayloadAction<CustomInsuranceContract | null>): CustomInsuranceState => ({
    ...state, selected_contract: action.payload
});

export const customInsurance = createSlice({
    name: 'custom_insurance',
    initialState,
    reducers: {
        updateCustomInsuranceIsCreating: updateCustomInsuranceIsCreatingReducer,
        toggleCustomInsuranceRefreshList: toggleCustomInsuranceRefreshListReducer,
        addCustomInsuranceListConfigurationPreference: addCustomInsuranceListConfigurationPreferenceReducer,
        setCustomInsuranceListConfigurationPreference: setCustomInsuranceListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        setColumns: setColumnsReducer,
        updateCustomInsuranceList: updateCustomInsuranceListReducer,
        updateCustomInsuranceListCriteria: updateCustomInsuranceListCriteriaReducer,
        updateCustomInsurance: updateCustomInsuranceReducer,
        updateCustomInsuranceListLoading: updateCustomInsuranceListLoadingReducer,
        updateCustomInsuranceListCount: updateCustomInsuranceListCountReducer,
        updateCustomInsuranceProviders: updateCustomInsuranceProvidersReducer,
        updateCustomInsuranceCurrencies: updateCustomInsuranceCurrenciesReducer,
        updateCustomInsuranceConditionFields: updateCustomInsuranceConditionFieldsReducer,
        updateSelectedContract: updateSelectedContractReducer,
    },
    extraReducers: (builder) => {
        builder.addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    updateCustomInsuranceIsCreating,
    toggleCustomInsuranceRefreshList,
    addCustomInsuranceListConfigurationPreference,
    setCustomInsuranceListConfigurationPreference,
    toggleColumn,
    setColumns,
    updateCustomInsuranceList,
    updateCustomInsuranceListCriteria,
    updateCustomInsurance,
    updateCustomInsuranceListLoading,
    updateCustomInsuranceListCount,
    updateCustomInsuranceProviders,
    updateCustomInsuranceCurrencies,
    updateCustomInsuranceConditionFields,
    updateSelectedContract,
} = customInsurance.actions;
